import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getAccountUuid, isFeatureEnabled } from 'ff/common/ffAppUtils';

import { buildShareUrl, extractTrimmedValues } from 'ff/debrief/utils';
import { useDebriefDispatch } from 'ff/debrief/hooks';
import { actions as chronoActions } from 'ff/debrief/features/ChronoSlice';
import {
    useGetAllTracklogsQuery,
    useGetShareStateQuery,
    useGetTracklogByIdQuery,
    useMakeDebriefPrivateMutation,
    useMakeDebriefPublicMutation,
} from 'ff/debrief/features/TracklogApi';

interface CurrentTracklogOptions {
    autoSelect?: boolean;
}

export const useCurrentTracklog = ({
    autoSelect,
}: CurrentTracklogOptions = {}) => {
    const params = useParams<{ tracklogId: string }>();
    const navigate = useNavigate();

    const dispatch = useDebriefDispatch();

    const selectedTracklogId = params.tracklogId;
    const { data: tlList } = useGetAllTracklogsQuery({
        page: 0, // Page is ignored here
        uuid: selectedTracklogId,
    });
    const {
        data: tl,
        currentData: currentTl,
        ...tracklogQueryStatus
    } = useGetTracklogByIdQuery(selectedTracklogId, {
        skip: !selectedTracklogId,
    });
    const { currentData: share, ...shareQueryStatus } = useGetShareStateQuery(
        selectedTracklogId,
        {
            skip:
                !isFeatureEnabled('new_share_debrief_feature') ||
                !selectedTracklogId,
        }
    );
    const [setPublic] = useMakeDebriefPublicMutation();
    const [setPrivate] = useMakeDebriefPrivateMutation();

    const isPublic = shareQueryStatus.isSuccess;

    // Auto select first tracklog on initial load
    const isListLoaded = Boolean(tlList) && tlList.tracklogs.length > 0;
    React.useEffect(() => {
        if (autoSelect && isListLoaded && !selectedTracklogId) {
            setTracklog(tlList.tracklogs[0].trackUuid);
        }
    }, [autoSelect, isListLoaded, selectedTracklogId]);

    React.useEffect(() => {
        if (
            autoSelect &&
            isListLoaded &&
            selectedTracklogId &&
            tracklogQueryStatus.isSuccess
        ) {
            const found = tlList.tracklogs.find(
                t => t.trackUuid === selectedTracklogId
            );
            if (!found) {
                setTracklog(tlList.tracklogs[0].trackUuid);
            }
        }
    }, [isListLoaded, selectedTracklogId, tracklogQueryStatus.isError]);

    const setTracklog = (trackId: string) => {
        navigate(`/${trackId}`);
        dispatch(chronoActions.reset());
    };
    const deselect = () => {
        const selectedIndex = tlList?.tracklogs.findIndex(
            t => t.trackUuid === selectedTracklogId
        );
        const nextTracklog = tlList?.tracklogs[selectedIndex + 1];
        const prevTracklog = tlList?.tracklogs[selectedIndex - 1];
        if (nextTracklog) setTracklog(nextTracklog.trackUuid);
        else if (prevTracklog) setTracklog(prevTracklog.trackUuid);
        else setTracklog('');
    };

    const setShareState = (newPublic: boolean) => {
        if (newPublic) {
            return setPublic(selectedTracklogId).unwrap();
        } else {
            return setPrivate(share.shareId).unwrap();
        }
    };

    const shareUrl = buildShareUrl(selectedTracklogId, share?.shareId);

    return {
        tracklog: { ...tl, trackUuid: selectedTracklogId },
        currentTracklog: currentTl,
        trimmedValues: extractTrimmedValues(tl),
        metadata: tl?.metadata,
        hasCloudAhoyDebrief: Boolean(
            tl?.analysisSummary && tl?.analysisSummary.status !== 'ERROR'
        ),
        shareUrl: shareUrl,
        isFailedUpload: tracklogQueryStatus.isError,
        isOwnTracklog: getAccountUuid() === tl?.accountUuid,
        isLoaded: !tracklogQueryStatus.isFetching,
        accuracy: tl?.accuracy,
        points: tl?.points,
        coordinates: tl?.coordinates,
        viewport: tl?.viewport,
        isPublic: isPublic,
        setTracklog: setTracklog,
        setShareState: setShareState,
        deselect: deselect,
        status: tracklogQueryStatus,
    };
};
